import { Controller } from '@hotwired/stimulus';
import { capitalize, debounce } from 'es-toolkit/compat';

const SCORE_STRENGTH = { 0: 'weak', 1: 'weak', 2: 'fair', 3: 'good', 4: 'strong' } as const;

function getClassName(strength: string): string {
  return `PasswordStrengthMeter--${strength}`;
}

function getSuggestion(strength: string, suggestions: string[]): string {
  const suggestion = suggestions[suggestions.length - 1];

  if (suggestion === 'Add another word or two. Uncommon words are better.') {
    return 'Add an uncommon word or two';
  }
  if (strength === 'good') {
    return 'Could be stronger';
  }

  return suggestion ?? '';
}

export default class extends Controller {
  static targets = ['input', 'meter', 'score', 'suggestion'];

  declare inputTarget: HTMLInputElement;

  declare meterTarget: HTMLElement;

  declare scoreTarget: HTMLElement;

  declare suggestionTarget: HTMLElement;

  #zxcvbn?: typeof import('zxcvbn');

  async connect(): Promise<void> {
    this.#zxcvbn = (await import('zxcvbn')).default;
  }

  disconnect(): void {
    this.change.cancel();
  }

  change = debounce(() => this.#handleChange(), 200);

  #handleChange(): void {
    const { value } = this.inputTarget;

    if (!this.#zxcvbn) return;
    if (value.length !== 0 && value.length < 4) return;

    // password strength calculation has quadratic time complexity
    // let's cut it off at the first 100 characters
    const result = this.#zxcvbn(value.substring(0, 100));
    const { suggestions } = result.feedback;
    const strength = value.length < 8 ? 'weak' : SCORE_STRENGTH[result.score];

    this.meterTarget.classList.remove(...Object.values(SCORE_STRENGTH).map(getClassName));
    this.meterTarget.classList.add(getClassName(strength));
    this.scoreTarget.textContent = `${capitalize(strength)} Password`;
    this.suggestionTarget.textContent = getSuggestion(strength, suggestions);
  }
}
